import Facade from '@/Facade'
import * as PIXI from 'pixi.js'

/**
 * Where the tournament badge hangs from: the Sound button of the top-right HUD row.
 *
 * The badge is a fixed-positioned HTML element owned by the external UI, while the row it has to
 * sit under is drawn inside the canvas, at a scale and offset that depend on the viewport. A
 * hand-tuned CSS offset matched exactly one aspect ratio and drifted onto the reels everywhere
 * else, so the button reports itself here and the offsets are measured from where it actually is.
 */

export type BadgeAnchorRect = { left: number; top: number; width: number; height: number }

let anchorElement: PIXI.Container | null = null
let cachedRect: BadgeAnchorRect | null = null
let cachedViewportKey = ''

const resolveViewportKey = (): string => {
    if (typeof window === 'undefined') return ''

    const width = Math.round(window.visualViewport?.width || window.innerWidth || 0)
    const height = Math.round(window.visualViewport?.height || window.innerHeight || 0)

    return `${width}x${height}`
}

/** Called by the panel whenever the row is laid out, which is also when the measurement goes stale. */
export const registerTournamentBadgeAnchor = (element?: PIXI.Container | null): void => {
    if (!element) {
        return
    }

    anchorElement = element
    cachedRect = null
    cachedViewportKey = ''
}

const measureAnchorRect = (): BadgeAnchorRect | null => {
    const element = anchorElement

    if (!element || element.destroyed) {
        return null
    }

    const app = Facade.app as any
    const canvas = (app?.canvas || app?.view) as HTMLCanvasElement | undefined
    const screen = app?.screen

    if (!canvas || !screen?.width || !screen?.height) {
        return null
    }

    const canvasRect = canvas.getBoundingClientRect()
    if (!canvasRect.width || !canvasRect.height) {
        return null
    }

    const bounds = element.getBounds()
    const boundsWidth = Number(bounds.width)
    const boundsHeight = Number(bounds.height)

    if (!(boundsWidth > 0) || !(boundsHeight > 0)) {
        return null
    }

    const scaleX = canvasRect.width / screen.width
    const scaleY = canvasRect.height / screen.height

    return {
        left: canvasRect.left + Number(bounds.x) * scaleX,
        top: canvasRect.top + Number(bounds.y) * scaleY,
        width: boundsWidth * scaleX,
        height: boundsHeight * scaleY,
    }
}

/**
 * Measured lazily and kept until the viewport changes: the snapshot this feeds is pushed on every
 * repo update, several times per spin, and reading the canvas rect forces a layout each time.
 *
 * The last good measurement is kept as the answer while the row is between layouts, so the badge
 * stays where it is instead of jumping back to the config offset for a frame.
 */
export const getTournamentBadgeAnchorRect = (): BadgeAnchorRect | null => {
    const viewportKey = resolveViewportKey()

    if (cachedRect && cachedViewportKey === viewportKey) {
        return cachedRect
    }

    const measured = measureAnchorRect()
    if (!measured) {
        return cachedRect
    }

    cachedRect = measured
    cachedViewportKey = viewportKey

    return cachedRect
}
